/**
 * Varejo · Visão Geral (issue #242) — KPIs do book + volume contratado/ano.
 *
 * Σ|CF| e duration vêm de useCurvaPnl().books.varejo — a MESMA fonte do card
 * CF por Book (#64): paridade por construção, sem recompute no backend novo.
 */
import { useState } from "react";
import { CalendarClock, Receipt, Users, Wallet } from "lucide-react";
import { KpiCard } from "../ui/KpiCard";
import { Loading } from "../Loading";
import { useCurvaPnl } from "../../hooks/useCurvaPnl";
import { useVarejoResumo } from "../../hooks/useVarejo";
import { AsOfBadge } from "../ui/AsOfBadge";
import { fmtMWm, fmtNum } from "../../utils/formatters";

function fmtMM(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `R$ ${(v / 1_000_000).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}MM`;
}

export function VisaoGeralTab() {
  const resumo = useVarejoResumo();
  const pnl = useCurvaPnl();
  // #244: a tabela de volume/ano default só mostra o ano corrente em diante;
  // histórico (anos já passados) vira toggle opt-in.
  const [mostrarAnteriores, setMostrarAnteriores] = useState(false);

  if (resumo.isLoading && !resumo.data) {
    return <Loading label="Carregando resumo do Varejo…" />;
  }
  if (resumo.error || !resumo.data) {
    return (
      <div className="text-sm text-indra-negativo">
        Falha ao carregar o resumo do Varejo — tente Atualizar.
      </div>
    );
  }

  const r = resumo.data;
  const varejoBook = pnl.data?.books?.varejo ?? null;

  // Ano corrente vem do `metadados.hoje` do payload (YYYY-MM-DD) — fonte única,
  // sem `new Date()` local (determinístico p/ teste e imune a fuso). O ano
  // corrente entra INTEIRO (não só meses restantes) — anotado no rodapé.
  const anoCorrente = Number(r.metadados.hoje.slice(0, 4));
  const anosTodos = r.volume_por_ano;
  const temAnteriores = anosTodos.some((v) => v.ano < anoCorrente);
  const anosVisiveis = mostrarAnteriores
    ? anosTodos
    : anosTodos.filter((v) => v.ano >= anoCorrente);

  return (
    <div className="space-y-6">
      {/* #60: as-of do DADO servido (snapshot diário do precompute / live) */}
      <div className="flex justify-end -mb-4">
        <AsOfBadge asOf={r.as_of ?? null} />
      </div>
      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label="Clientes ativos"
          value={String(r.n_clientes)}
          sub="unidades com trade vigente"
          subTone="muted"
          icon={<Users size={16} />}
          testId="varejo-kpi-clientes"
        />
        <KpiCard
          label="Receita contratada (próx. 12m)"
          value={fmtMM(r.receita_contratada_12m)}
          sub="Σ vendas · fornecimento nos próximos 12 meses"
          subTone="muted"
          icon={<Receipt size={16} />}
          tooltip="Acumulado das vendas contratadas cujo mês de FORNECIMENTO cai nos próximos 12 meses. Atenção: fornecimento ≠ pagamento — o caixa (6DU) vence depois do mês fornecido."
          highlight
          testId="varejo-kpi-receita"
        />
        <KpiCard
          label="Σ|CF| do book"
          value={varejoBook ? fmtMM(varejoBook.sigma_cf) : "—"}
          sub={
            varejoBook && varejoBook.duration !== null
              ? `duration ${fmtNum(varejoBook.duration)} anos · fonte: CF por Book (#64)`
              : "fonte: CF por Book (#64)"
          }
          subTone="muted"
          icon={<Wallet size={16} />}
          testId="varejo-kpi-sigma-cf"
        />
        <KpiCard
          label="Vencimentos 12m"
          value={`${r.vencimentos_12m.n_contratos} contratos`}
          sub={`${fmtMWm(r.vencimentos_12m.mwm_saindo)} saindo do book`}
          subTone={r.vencimentos_12m.n_contratos > 0 ? "negative" : "muted"}
          icon={<CalendarClock size={16} />}
          testId="varejo-kpi-vencimentos"
        />
      </div>

      <div className="rounded-xl border border-white/10 bg-indra-azul-surface p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="text-[11px] uppercase tracking-wider text-white/60">
            Volume contratado por ano (vendas)
          </div>
          {temAnteriores && (
            <label className="flex items-center gap-1.5 text-[11px] text-white/50 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={mostrarAnteriores}
                onChange={(e) => setMostrarAnteriores(e.target.checked)}
                data-testid="varejo-volume-ano-toggle"
                className="accent-indra-turquesa"
              />
              mostrar anos anteriores
            </label>
          )}
        </div>
        <table className="w-full text-sm" data-testid="varejo-volume-ano">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-700/40">
              <th className="py-2">Ano</th>
              <th className="py-2 text-right">MWh</th>
              <th className="py-2 text-right">MWm</th>
            </tr>
          </thead>
          <tbody>
            {anosVisiveis.map((v) => (
              <tr
                key={v.ano}
                className="border-b border-slate-700/20 hover:bg-white/5"
                data-testid={v.ano < anoCorrente ? "varejo-volume-ano-row-passado" : "varejo-volume-ano-row"}
              >
                <td className="py-2">{v.ano}</td>
                <td className="py-2 text-right font-mono">
                  {v.mwh.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}
                </td>
                <td className="py-2 text-right font-mono">{fmtNum(v.mwm, 3)}</td>
              </tr>
            ))}
            {anosVisiveis.length === 0 && (
              <tr>
                <td colSpan={3} className="py-3 text-white/40">
                  {anosTodos.length === 0
                    ? "Sem vendas vigentes no book."
                    : "Sem anos a partir do corrente — ative “mostrar anos anteriores”."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="text-[11px] text-white/30 mt-3">
          {r.metadados.universo} · o ano corrente ({anoCorrente}) aparece inteiro
          (jan–dez), não só os meses restantes.
        </div>
      </div>
    </div>
  );
}
